import React, { CSSProperties, useId } from 'react';

import {
  formatCents,
  formatOrderDay,
  frequencyPhrase,
  offerDiscountLabel,
  offerOption,
  type SubscribeAndSaveOffer,
  type SubscriptionFrequency,
} from './subscribeAndSave';

const MUTED = 'rgba(0, 0, 0, 0.5)';

const selectStyles: Record<'sm' | 'md', { label: CSSProperties; select: CSSProperties }> = {
  sm: {
    label: { fontSize: 12, lineHeight: '16px' },
    select: {
      padding: '4px 8px',
      border: '0.5px solid #dcdddc',
      borderRadius: 6,
      fontSize: 10,
      fontWeight: 500,
      lineHeight: '16px',
    },
  },
  md: {
    label: { fontSize: 13 },
    select: { padding: '4px 8px', border: '1px solid rgba(0, 0, 0, 0.15)', borderRadius: 8, fontSize: 13 },
  },
};

/**
 * The frequency select of a subscription. It lists the offer's frequencies. A frequency the offer does not list stays
 * selectable, so the select always shows the frequency the line has.
 */
export const FrequencySelect = ({
  label,
  size = 'md',
  frequency,
  offer,
  onChange,
}: {
  label: string;
  /** `sm` is the compact select of a bag line. */
  size?: 'sm' | 'md';
  frequency: SubscriptionFrequency;
  offer?: SubscribeAndSaveOffer;
  onChange: (frequency: SubscriptionFrequency) => void;
}) => {
  const id = useId();
  const options = offer?.options.map((option) => option.frequency) ?? [];
  const selected = offerOption(offer, frequency);
  const index = selected ? offer!.options.indexOf(selected) : -1;
  const styles = selectStyles[size];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <label htmlFor={id} style={styles.label}>
        {label}
      </label>
      <select
        id={id}
        style={{ background: 'transparent', color: 'inherit', font: 'inherit', ...styles.select }}
        value={index}
        onChange={(event) => {
          const next = options[Number(event.target.value)];
          if (next) onChange(next);
        }}
      >
        {index < 0 ? <option value={-1}>{frequencyPhrase(frequency)}</option> : null}
        {options.map((option, optionIndex) => (
          <option key={optionIndex} value={optionIndex}>
            {frequencyPhrase(option)}
          </option>
        ))}
      </select>
    </span>
  );
};

/** The line above the title of a subscription line in the bag. */
export const SubscriptionEyebrow = ({ offer }: { offer?: SubscribeAndSaveOffer }) => (
  <span style={{ fontSize: 10, fontWeight: 500, lineHeight: '20px' }}>
    {offer ? `Subscribe & Save • ${offerDiscountLabel(offer)}` : 'Subscribe & Save'}
  </span>
);

/** The Delivery select and the next order of a subscription line in the bag. */
export const SubscriptionLineDetails = ({
  frequency,
  offer,
  onChange,
}: {
  frequency: SubscriptionFrequency;
  offer?: SubscribeAndSaveOffer;
  onChange: (frequency: SubscriptionFrequency) => void;
}) => {
  const nextOrderDate = offerOption(offer, frequency)?.nextOrderDate;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginTop: 4,
        paddingTop: 8,
        borderTop: '1px solid #dcdcdc',
      }}
    >
      <FrequencySelect label="Delivery" size="sm" frequency={frequency} offer={offer} onChange={onChange} />
      {nextOrderDate ? (
        <span style={{ fontSize: 10, lineHeight: '16px' }}>
          {`Next order arrives around ${formatOrderDay(nextOrderDate, 'long')}.`}
        </span>
      ) : null}
    </div>
  );
};

/** The subscriber price of a line, with the one-time price struck after it. */
export const SubscriberPrice = ({ offer }: { offer: SubscribeAndSaveOffer }) => (
  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
    <span>{formatCents(offer.subscriberUnitPriceCents)}</span>
    <s style={{ fontSize: 10, fontWeight: 400, color: MUTED }}>{formatCents(offer.unitPriceCents)}</s>
  </span>
);
